import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Popconfirm,
  message,
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Upload,
  Image,
  Switch,
  Card
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  PlusOutlined,
  MinusOutlined
} from '@ant-design/icons';
import { supabase } from '../../config/supabaseClient';
import JoditEditor from 'jodit-react';
import { debounce } from 'lodash';

const { Option } = Select;

const ManageProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form] = Form.useForm();
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [classes, setClasses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [mcqs, setMcqs] = useState([]);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [notesFile, setNotesFile] = useState(null);
  const [notesUploading, setNotesUploading] = useState(false);
  const [isMCQ, setIsMCQ] = useState(false);
  const editor = useRef(null);

  const debouncedDescriptionChange = useRef(
    debounce((newContent) => {
      setDescription(newContent);
    }, 300)
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchClasses();
    fetchCategories();
    fetchSubCategories();
  }, []);

  const fetchClasses = async () => {
    try {
      const { data, error } = await supabase.from('classes').select('*');
      if (error) throw error;
      setClasses(data || []);
    } catch (error) {
      message.error('Failed to fetch classes.');
      console.error(error);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase.from('categories').select('*');
      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchSubCategories = async () => {
    try {
      const { data, error } = await supabase.from('subcategories').select('*');
      if (error) throw error;
      setSubCategories(data || []);
    } catch (error) {
      console.error('Failed to fetch subcategories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('topics')
        .select('*')
        .order('timestamp', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      message.error('Failed to fetch products.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      const productToDelete = products.find((p) => p.id === id);

      // Delete featured image if exists
      if (productToDelete?.featuredImage) {
        try {
          // Extract path from URL? Or store path? Usually better to store path.
          // Assuming the URL is public, we need to extract the path.
          // If stored as full URL: https://.../featured-images/filename
          // Supabase path: featured-images/filename
          // For now, let's assume we can try to extract it or just skip if complex.
          // But strict logic requires deletion.
          // Simple regex to get path after bucket name if possible.
          const path = productToDelete.featuredImage.split('/storage/v1/object/public/featured-images/')[1];
          if (path) {
            await supabase.storage.from('featured-images').remove([path]);
          }
        } catch (err) {
          console.warn('No featured image or already deleted:', err.message);
        }
      }

      // Delete notes file if exists
      if (productToDelete?.notesFile) {
        try {
          const path = productToDelete.notesFile.split('/storage/v1/object/public/notes-files/')[1];
          if (path) {
            await supabase.storage.from('notes-files').remove([path]);
          }
        } catch (err) {
          console.warn('No notes file or already deleted:', err.message);
        }
      }

      // Delete Supabase row
      const { error } = await supabase.from('topics').delete().eq('id', id);
      if (error) throw error;

      setProducts((prev) => prev.filter((product) => product.id !== id));
      message.success('Product and associated files deleted successfully!');
    } catch (error) {
      message.error('Failed to delete product.');
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  const handleEdit = (record) => {
    setEditingProduct(record);
    setDescription(record.description || '');
    setMcqs(record.mcqs || []);
    setIsMCQ(record.mcqs && record.mcqs.length > 0);
    setFeaturedImage(record.featuredImage || null);
    setNotesFile(record.notesFile || null);

    // Parse class string back to array if it's stored as comma-separated
    const classArray = record.class ? record.class.split(',').map(c => c.trim()) : [];

    form.setFieldsValue({
      topic: record.topic,
      class: classArray,
      category: record.category || '',
      subCategory: record.subCategory || '',
    });
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingProduct(null);
    setDescription('');
    setMcqs([]);
    setIsMCQ(false);
    setFeaturedImage(null);
    setNotesFile(null);
    form.resetFields();
    setLoading(false);
  };

  const uploadFileToSupabase = async (file, bucket, onProgress) => {
    try {
      const uniqueFileName = `${Date.now()}-${file.name.replace(/\s+/g, "-")}`;
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(uniqueFileName, file, {
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
              onProgress(percent);
            }
          }
        });

      if (error) throw error;

      const { data: publicUrlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(uniqueFileName);

      return publicUrlData.publicUrl;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  const uploadFeaturedImage = async (file) => {
    setImageUploading(true);
    try {
      const downloadURL = await uploadFileToSupabase(file, "featured-images");
      setFeaturedImage(downloadURL);
      message.success('Featured image uploaded!');
      return downloadURL;
    } catch (error) {
      message.error('Featured image upload failed.');
      throw error;
    } finally {
      setImageUploading(false);
    }
  };

  const uploadNotesFile = async (file) => {
    setNotesUploading(true);
    try {
      const downloadURL = await uploadFileToSupabase(file, "notes-files");
      setNotesFile(downloadURL);
      message.success('Notes file uploaded!');
      return downloadURL;
    } catch (error) {
      message.error('Notes file upload failed.');
      throw error;
    } finally {
      setNotesUploading(false);
    }
  };

  const handleImageUpload = async (options) => {
    const { file } = options;
    try {
      await uploadFeaturedImage(file);
    } catch (error) {
      console.error("Image upload error:", error);
    }
  };

  const handleNotesUpload = async (options) => {
    const { file } = options;
    try {
      await uploadNotesFile(file);
    } catch (error) {
      console.error("Notes upload error:", error);
    }
  };

  const handleJoditImageUpload = (editor, file) => {
    const key = 'jodit-image-upload';
    return new Promise((resolve, reject) => {
      message.loading({ content: 'Uploading image... 0%', key, duration: 0 });

      uploadFileToSupabase(file, 'featured-images', (percent) => {
        message.loading({ content: `Uploading image... ${percent}%`, key, duration: 0 });
      })
      .then(downloadURL => {
        message.success({ content: 'Image uploaded successfully!', key, duration: 2 });
        resolve(downloadURL);
      })
      .catch(error => {
        uploadFileToSupabase(file, 'editor-images', (percent) => {
          message.loading({ content: `Uploading image... ${percent}%`, key, duration: 0 });
        })
        .then(downloadURL => {
          message.success({ content: 'Image uploaded successfully!', key, duration: 2 });
          resolve(downloadURL);
        })
        .catch(fallbackError => {
          message.error({ content: 'Image upload failed', key, duration: 2 });
          console.error('Image upload error:', fallbackError);
          reject(fallbackError);
        });
      });
    });
  };

  const handleJoditFileUpload = (editor, file) => {
    const key = 'jodit-file-upload';
    return new Promise((resolve, reject) => {
      message.loading({ content: 'Uploading file... 0%', key, duration: 0 });

      uploadFileToSupabase(file, 'notes-files', (percent) => {
        message.loading({ content: `Uploading file... ${percent}%`, key, duration: 0 });
      })
      .then(downloadURL => {
        message.success({ content: 'File uploaded successfully!', key, duration: 2 });
        resolve(downloadURL);
      })
      .catch(error => {
        uploadFileToSupabase(file, 'editor-files', (percent) => {
          message.loading({ content: `Uploading file... ${percent}%`, key, duration: 0 });
        })
        .then(downloadURL => {
          message.success({ content: 'File uploaded successfully!', key, duration: 2 });
          resolve(downloadURL);
        })
        .catch(fallbackError => {
          uploadFileToSupabase(file, 'editor-images', (percent) => {
            message.loading({ content: `Uploading file... ${percent}%`, key, duration: 0 });
          })
          .then(downloadURL => {
            message.success({ content: 'File uploaded successfully!', key, duration: 2 });
            resolve(downloadURL);
          })
          .catch(err => {
            message.error({ content: 'File upload failed', key, duration: 2 });
            console.error('File upload error:', err);
            reject(err);
          });
        });
      });
    });
  };

  const handleUpdate = async (values) => {
    setLoading(true);
    try {
      const updatedValues = {
        topic: values.topic,
        class: Array.isArray(values.class) ? values.class.join(", ") : values.class,
        category: values.category, // Explicitly map values
        "subCategory": values.subCategory, // Quote if needed for case sensitivity in JS object, though strictly standard in JS.
        description,
        mcqs: isMCQ ? mcqs : [],
        "featuredImage": featuredImage,
        "notesFile": notesFile,
        timestamp: new Date().toISOString(), // Supabase compatible timestamp
      };

      const { error } = await supabase
        .from('topics')
        .update(updatedValues)
        .eq('id', editingProduct.id);

      if (error) throw error;

      message.success('Product updated successfully!');
      handleModalClose();
      fetchProducts();
    } catch (error) {
      message.error('Failed to update product.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMCQChange = (index, key, value) => {
    const updatedMcqs = [...mcqs];
    updatedMcqs[index][key] = value;
    setMcqs(updatedMcqs);
  };

  const handleOptionChange = (mcqIndex, optionIndex, value) => {
    const updatedMcqs = [...mcqs];
    updatedMcqs[mcqIndex].options[optionIndex] = value;
    setMcqs(updatedMcqs);
  };

  const handleCorrectAnswerChange = (mcqIndex, value) => {
    const updatedMcqs = [...mcqs];
    updatedMcqs[mcqIndex].correctAnswer = value;
    setMcqs(updatedMcqs);
  };

  const handleAddMCQ = () => {
    setMcqs([
      ...mcqs,
      { question: "", options: ["", "", "", ""], correctAnswer: "", logic: "" },
    ]);
  };

  const handleRemoveMCQ = (index) => {
    if (mcqs.length > 1) {
      const updatedMcqs = [...mcqs];
      updatedMcqs.splice(index, 1);
      setMcqs(updatedMcqs);
    } else {
      message.warning("At least one MCQ is required");
    }
  };

  const renderMCQTemplate = () => {
    return mcqs.map((mcq, index) => (
      <Card
        key={index}
        style={{ marginBottom: 16, border: "1px solid #f0f0f0", marginTop: 16 }}
        size="small"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, marginTop: 16 }}>
          <h4>MCQ {index + 1}</h4>
          <Button
            type="link"
            danger
            icon={<MinusOutlined />}
            onClick={() => handleRemoveMCQ(index)}
            disabled={mcqs.length <= 1}
          >
            Remove
          </Button>
        </div>

        <Form.Item label="Question" required>
          <JoditEditor
            value={mcq.question}
            config={mcqJoditConfig}
            onChange={(newContent) => handleMCQChange(index, "question", newContent)}
          />
        </Form.Item>

        <Form.Item label="Options" required>
          {mcq.options.map((option, optionIndex) => (
            <div key={optionIndex} style={{ marginBottom: 8 }}>
              <Input
                addonBefore={
                  <input
                    type="radio"
                    name={`correct-${index}`}
                    checked={mcq.correctAnswer === option}
                    onChange={() => handleCorrectAnswerChange(index, option)}
                  />
                }
                value={option}
                onChange={(e) => handleOptionChange(index, optionIndex, e.target.value)}
                placeholder={`Option ${optionIndex + 1}`}
              />
            </div>
          ))}
        </Form.Item>

        <Form.Item label="Logic for Correct Answer (Optional)">
          <JoditEditor
            value={mcq.logic}
            config={mcqJoditConfig}
            onChange={(newContent) => handleMCQChange(index, "logic", newContent)}
          />
        </Form.Item>
      </Card>
    ));
  };

  const joditConfig = {
    readonly: false,
    height: 300,
    buttons: [
      "source", "|", "bold", "italic", "underline", "strikethrough", "|",
      "ul", "ol", "|", "font", "fontsize", "brush", "paragraph", "|",
      "align", "outdent", "indent", "|", "cut", "copy", "paste", "copyformat", "|",
      "hr", "table", "link", "|", "undo", "redo", "|", "preview", "print",
      "find", "fullsize", "image", "uploadImage", "video", "file", "uploadFile"
    ],
    uploader: {
      insertImageAsBase64URI: false,
      url: false,
      imagesExtensions: ["jpg", "png", "jpeg", "gif"],
      withCredentials: false,
    },
    imageDefaultWidth: 300,
    spellcheck: true,
    toolbarAdaptive: false,
    placeholder: "Type your content here...",
    image: {
      dialog: {
        tabs: {
          'Upload': {
            fields: {
              file: {
                name: 'file',
                label: 'Upload image from computer',
                type: 'file',
                accept: 'image/*',
                required: true
              }
            }
          },
          'URL': {
            fields: {
              url: {
                name: 'url',
                label: 'URL',
                type: 'text',
                required: true
              }
            }
          }
        }
      },
      upload: async (file) => {
        try {
          const url = await handleJoditImageUpload(null, file);
          return { files: [url], isImages: [true] };
        } catch (error) {
          throw new Error('Upload failed');
        }
      },
      insert: function (url, alt, styles) {
        const image = this.selection.j.createInside.element('img');
        image.setAttribute('src', url);
        image.setAttribute('alt', url);
        this.s.insertNode(image);
      }
    },
    file: {
      dialog: {
        tabs: {
          'Upload': {
            fields: {
              file: {
                name: 'file',
                label: 'Upload file from computer',
                type: 'file',
                required: true
              }
            }
          },
          'URL': {
            fields: {
              url: {
                name: 'url',
                label: 'URL',
                type: 'text',
                required: true
              }
            }
          }
        }
      },
      upload: async (file) => {
        try {
          const url = await handleJoditFileUpload(null, file);
          return { files: [url] };
        } catch (error) {
          throw new Error('Upload failed');
        }
      },
      insert: function (url, title) {
        const link = this.selection.j.createInside.element('a');
        link.setAttribute('href', url);
        link.setAttribute('target', '_blank');
        link.innerText = title || 'Download File';
        this.s.insertNode(link);
      }
    },
    controls: {
      uploadImage: {
        icon: 'image',
        exec: function (editor) {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = 'image/*';
          input.onchange = async () => {
            if (input.files && input.files[0]) {
              try {
                const file = input.files[0];
                const url = await handleJoditImageUpload(null, file);
                
                const image = document.createElement('img');
                image.src = url;
                image.alt = url;
                editor.selection.insertNode(image);
              } catch (err) {
                console.error("Upload failed", err);
              }
            }
          };
          input.click();
        },
        tooltip: 'Upload Image from PC'
      },
      uploadFile: {
        icon: 'file',
        exec: function (editor) {
          const input = document.createElement('input');
          input.type = 'file';
          input.onchange = async () => {
            if (input.files && input.files[0]) {
              try {
                const file = input.files[0];
                const url = await handleJoditFileUpload(null, file);
                
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.innerText = file.name || 'Download File';
                editor.selection.insertNode(link);
              } catch (err) {
                console.error("Upload failed", err);
              }
            }
          };
          input.click();
        },
        tooltip: 'Upload File from PC'
      }
    }
  };

  const mcqJoditConfig = {
    ...joditConfig,
    height: 150,
    buttons: "bold,italic,underline,strikethrough,ul,ol,font,fontsize,brush,paragraph,align,link,image",
  };

  const columns = [
    {
      title: 'Topic',
      dataIndex: 'topic',
      key: 'topic',
      render: (text) => text || 'No Topic'
    },
    {
      title: 'Class',
      dataIndex: 'class',
      key: 'class',
      render: (text) => text || 'No Class'
    },
    {
      title: 'Notes File',
      key: 'notesFile',
      render: (_, record) =>
        record.notesFile ? (
          <a href={record.notesFile} target="_blank" rel="noopener noreferrer">
            View Notes
          </a>
        ) : (
          <span>No File</span>
        ),
    },
    {
      title: 'SubCategory',
      dataIndex: 'subCategory',
      key: 'subCategory',
      render: (text) => text || 'No Subcategory'
    },
    {
      title: 'Featured Image',
      key: 'featuredImage',
      render: (_, record) =>
        record.featuredImage ? (
          <Image src={record.featuredImage} width={50} height={50} style={{ objectFit: 'cover' }} />
        ) : (
          <span>No Image</span>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text) => text || 'published'
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            loading={loading}
          />
          <Popconfirm
            title="Are you sure you want to delete this topic?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} loading={deleting} danger />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <div style={{ padding: 24 }}>
        <Card title="Categories & Subcategories Content Status" style={{ marginBottom: 24 }}>
          {categories.length === 0 ? (
            <div>No categories found.</div>
          ) : (
            (() => {
              const activeCategories = categories
                .map(cat => {
                  const categoryTopics = products.filter(p => p.category === cat.name);
                  const subCats = subCategories.filter(sub => sub.category_id === cat.id);
                  const activeSubCats = subCats
                    .map(sub => {
                      const subTopics = categoryTopics.filter(p => p.subCategory === sub.name);
                      return { ...sub, count: subTopics.length };
                    })
                    .filter(sub => sub.count > 0);

                  return { ...cat, count: categoryTopics.length, subCategories: activeSubCats };
                })
                .filter(cat => cat.count > 0);

              if (activeCategories.length === 0) {
                return <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>No categories with content available.</div>;
              }

              return activeCategories.map(cat => (
                <div key={cat.id} style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h5 style={{ fontWeight: 'bold', margin: 0 }}>{cat.name}</h5>
                    <span style={{ 
                      background: '#e6f7ff', 
                      color: '#1890ff', 
                      padding: '2px 8px', 
                      borderRadius: '10px', 
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {cat.count} Topics
                    </span>
                  </div>
                  <div style={{ paddingLeft: 16, marginTop: 8 }}>
                    {cat.subCategories.length > 0 ? (
                      cat.subCategories.map(sub => (
                        <div key={sub.id} style={{ fontSize: '14px', marginBottom: 4, display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: '#555' }}>- {sub.name}</span>
                          <span style={{ 
                            background: '#f6ffed', 
                            color: '#52c41a', 
                            padding: '1px 6px', 
                            borderRadius: '10px', 
                            fontSize: '11px'
                          }}>
                            {sub.count}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div style={{ fontSize: '14px', color: '#999' }}>No subcategories with content</div>
                    )}
                  </div>
                </div>
              ));
            })()
          )}
        </Card>

        <Table
          dataSource={products}
          columns={columns}
          rowKey="id"
          bordered
          pagination={{ pageSize: 10 }}
          loading={loading}
        />

        <Modal
          title="Edit Topic"
          open={isModalVisible}
          onCancel={handleModalClose}
          footer={null}
          width={1000}
          style={{ top: 20 }}
        >
          <Form form={form} layout="vertical" onFinish={handleUpdate}>
            <Form.Item label="Featured Image (Optional)">
              <Upload
                customRequest={handleImageUpload}
                showUploadList={false}
                accept="image/*"
              >
                <Button icon={<UploadOutlined />} loading={imageUploading}>
                  {imageUploading ? "Uploading..." : "Change Featured Image"}
                </Button>
              </Upload>
              {featuredImage && (
                <div style={{ marginTop: 16 }}>
                  <Image src={featuredImage} width={200} />
                  <Button
                    type="link"
                    danger
                    onClick={() => setFeaturedImage(null)}
                    style={{ marginTop: 8 }}
                  >
                    Remove Image
                  </Button>
                </div>
              )}
            </Form.Item>

            <Form.Item label="Notes File">
              <Upload
                customRequest={handleNotesUpload}
                showUploadList={false}
                accept=".pdf"
              >
                <Button icon={<UploadOutlined />} loading={notesUploading}>
                  {notesUploading ? "Uploading..." : "Change Notes File"}
                </Button>
              </Upload>
              {notesFile && (
                <div style={{ marginTop: 8 }}>
                  <a href={notesFile} target="_blank" rel="noopener noreferrer" style={{ marginRight: 8 }}>
                    View Notes File
                  </a>
                  <Button type="link" danger onClick={() => setNotesFile(null)}>
                    Remove File
                  </Button>
                </div>
              )}
            </Form.Item>

            <Form.Item
              name="topic"
              label="Topic Name"
              rules={[{ required: true, message: "Please enter topic name!" }]}
            >
              <Input placeholder="Enter topic name" />
            </Form.Item>

            <Form.Item
              name="class"
              label="Class"
              rules={[{ required: true, message: "Please select a class!" }]}
            >
              <Select
                mode="multiple"
                placeholder="Select class(es)"
              >
                {classes.map((cls) => (
                  <Option key={cls.id} value={cls.name}>
                    {cls.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="category"
              label="Category"
            >
              <Select placeholder="Select category">
                {categories.map((cat) => (
                  <Option key={cat.id} value={cat.name}>
                    {cat.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="subCategory"
              label="SubCategory"
            >
              <Select placeholder="Select subcategory">
                {subCategories.map((sub) => (
                  <Option key={sub.id} value={sub.name}>
                    {sub.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="MCQ Test">
              <Switch checked={isMCQ} onChange={(checked) => setIsMCQ(checked)} />
            </Form.Item>

            <Form.Item label="Description">
              <JoditEditor
                ref={editor}
                value={description}
                config={joditConfig}
                onBlur={(newContent) => setDescription(newContent)}
              />
            </Form.Item>

            {isMCQ && (
              <>
                {renderMCQTemplate()}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={handleAddMCQ}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add More MCQs
                  </Button>
                </Form.Item>
              </>
            )}

            <Form.Item style={{ marginTop: 24 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{ marginRight: 8 }}
              >
                Update Topic
              </Button>
              <Button onClick={handleModalClose}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>




    </>
  );
};

export default ManageProducts;